import { isEmpty, isSessionTimeExpired, isFutureDate } from "utility";
import { setSessionExpiryModalVisibility } from "redux/auth/actions";
import { updateMedicalAddress } from "redux/MedicalAlertUsers/actions";
import { FIELD_CONFIG } from "./config";
import alerts from "constants/alerts";
import moment from "moment";

/**
 * Calculate snackbar top position based on scroll
 */
export const calculateSnackbarTop = (scrollY) => {
  const headerHeight = 80;
  const stickyThreshold = 200;
  const initialOffset = 152; // Reduced from 168 (removed 16px card margin-bottom)
  const stickyOffset = 0;

  if (scrollY <= 0) {
    return headerHeight + initialOffset;
  } else if (scrollY < stickyThreshold) {
    const progress = scrollY / stickyThreshold;
    return headerHeight + initialOffset - (progress * (initialOffset - stickyOffset));
  } else {
    return headerHeight + stickyOffset;
  }
};

/**
 * Get region ID from region name/object
 */
export const getRegionId = (state, allRegionList) => {
  if (!state) return null;
  const stateId = typeof state === 'object' ? state?.id : state;
  const region = allRegionList?.find((ele) => {
    return parseInt(ele?.region_id) === parseInt(stateId);
  });
  return region?.region_id || null;
};

/**
 * Clear specific field error
 */
export const clearError = (fieldName, setErrorState) => {
  setErrorState(prev => ({
    ...prev,
    fieldErrors: { ...prev.fieldErrors, [fieldName]: false },
    fieldMessages: { ...prev.fieldMessages, [fieldName]: "" },
  }));
};

/**
 * Handle value change with validation
 */
export const handleValueChange = (type, value, params) => {
  const {
    subscriberState,
    setSubscriberState,
    validationTriggered,
    errorState,
    setErrorState,
    snackbarState,
    onApiResponse,
    dismissSnackbar
  } = params;

  setSubscriberState((prev) => {
    const newState = { ...prev, [type]: value };

    if (validationTriggered) {
      const config = FIELD_CONFIG?.[type];
      let fieldError = false;
      let fieldMessage = "";

      if (config?.validation?.required && isEmpty(value)) {
        fieldError = true;
        fieldMessage = config?.message || "";
      }

      // Special validation for zip_code length
      if (type === 'zip_code' && !isEmpty(value) && config?.validation?.length) {
        const zipValue = typeof value === 'string' || typeof value === 'number' ? `${value}`.trim() : '';
        if (zipValue.length !== config.validation.length) {
          fieldError = true;
          fieldMessage = alerts?.zipcode_length_error;
        }
      }

      // Special validation for dateOfBirth
      if (type === 'dateOfBirth' && !isEmpty(value) && isFutureDate(value)) {
        fieldError = true;
        fieldMessage = alerts?.future_birth_date;
      }

      setErrorState(prev => ({
        ...prev,
        show: true,
        fieldErrors: { ...prev?.fieldErrors, [type]: fieldError },
        fieldMessages: { ...prev?.fieldMessages, [type]: fieldMessage || "" }
      }));

      const updatedFieldErrors = { ...errorState?.fieldErrors, [type]: fieldError };
      const hasAnyErrors = Object.values(updatedFieldErrors || {}).some(Boolean);

      if (fieldError && !snackbarState.show) {
        onApiResponse(alerts?.incorrect_or_missing_details, "error");
      } else if (!fieldError && !hasAnyErrors && snackbarState.show) {
        dismissSnackbar();
      }
    } else {
      setErrorState(prev => {
        if (prev.fieldErrors?.[type]) {
          return {
            ...prev,
            fieldErrors: { ...prev.fieldErrors, [type]: false },
            fieldMessages: { ...prev.fieldMessages, [type]: "" },
          };
        }
        return prev;
      });

      if (snackbarState.show) {
        dismissSnackbar();
      }
    }

    return newState;
  });
};

/**
 * Validate all fields
 */
export const validateAllFields = (subscriberState, phoneList, VALIDATION_FIELDS) => {
  const fieldErrors = { ...VALIDATION_FIELDS };
  const fieldMessages = { ...VALIDATION_FIELDS };
  let hasErrors = false;
  let erroMessage = ""

  Object.keys(FIELD_CONFIG || {}).forEach(fieldName => {
    if (fieldName !== 'phoneList') {
      const value = subscriberState?.[fieldName];
      const config = FIELD_CONFIG?.[fieldName];

      if (config?.validation?.required && isEmpty(value)) {
        fieldErrors[fieldName] = true;
        fieldMessages[fieldName] = config?.message || "";
        erroMessage = config?.message
        hasErrors = true;
      }

      // Special validation for zip_code length
      if (fieldName === 'zip_code' && !isEmpty(value) && config?.validation?.length) {
        const zipValue = typeof value === 'string' || typeof value === 'number' ? `${value}`.trim() : '';
        if (zipValue.length !== config.validation.length) {
          fieldErrors[fieldName] = true;
          fieldMessages[fieldName] = alerts?.zipcode_length_error;
          erroMessage = alerts?.zipcode_length_error;
          hasErrors = true;
        }
      }

      // Special validation for dateOfBirth
      if (fieldName === 'dateOfBirth' && !isEmpty(value) && isFutureDate(value)) {
        fieldErrors[fieldName] = true;
        fieldMessages[fieldName] = alerts?.future_birth_date;
        erroMessage = alerts?.future_birth_date;
        hasErrors = true;
      }
    }
  });

  if (phoneList?.length === 0) {
    fieldErrors.phoneList = true;
    fieldMessages.phoneList = FIELD_CONFIG.phoneList?.message || "At least one phone number is required";
    erroMessage = FIELD_CONFIG.phoneList?.message
    hasErrors = true;
  }

  return { fieldErrors, fieldMessages, hasErrors, erroMessage };
};

/**
 * Handle submit
 */
export const handleSubmit = async (params) => {
  const {
    subscriberState,
    phoneList,
    phoneTypeList,
    medicalUserDetail,
    allRegionList,
    userData,
    setValidationTriggered,
    setErrorState,
    onApiResponse,
    dispatch,
    history,
    handleApiResponse,
    setChangesSaveModal,
    VALIDATION_FIELDS
  } = params;

  let timeDifference = await isSessionTimeExpired();

  setValidationTriggered(true);

  const { fieldErrors, fieldMessages, hasErrors, erroMessage } = validateAllFields(
    subscriberState,
    phoneList,
    VALIDATION_FIELDS
  );

  if (hasErrors) {
    setErrorState({
      show: true,
      fieldErrors,
      fieldMessages
    });
    onApiResponse(erroMessage ? erroMessage : alerts?.incorrect_or_missing_details, "error");
    return;
  }

  if (timeDifference) {
    setSessionExpiryModalVisibility(dispatch, true);
    return;
  } else {
    const selectedRegion = allRegionList?.find(
      (region) => parseInt(region?.region_id) === parseInt(subscriberState?.state?.id)
    );

    // Get reference phone type IDs from phone type list
    const getReferencePhoneTypeId = (typeId) => {
      const phoneType = phoneTypeList?.find(pt => parseInt(pt?.phone_type_id) === parseInt(typeId));
      return phoneType?.reference_phone_type_id || null;
    };

    // Clean phone numbers - remove formatting
    const cleanPhoneNumber = (phone) => {
      return phone ? phone.replace(/\D/g, "") : false;
    };

    let payload = {
      address_id: medicalUserDetail?.protected_address?.address_id,
      region_id: subscriberState?.state?.id,
      street: subscriberState?.street,
      street2: subscriberState?.apt,
      postcode: subscriberState?.zip_code,
      city: subscriberState?.city,
      reference_region_id: selectedRegion?.reference_region_id || null,
      date_of_birth: moment(subscriberState?.dateOfBirth)?.format("YYYY-MM-DD")?.toString(),
      mobile: cleanPhoneNumber(phoneList?.[0]?.phoneNumber),
      phone_type_id: phoneList?.[0]?.type_id || null,
      reference_phone_type_id: getReferencePhoneTypeId(phoneList?.[0]?.type_id),
      phone: cleanPhoneNumber(phoneList?.[1]?.phoneNumber),
      cell_phone_type_id: phoneList[1]?.type?.type_id
        ? phoneList[1]?.type?.type_id
        : phoneTypeList[0]?.type_id,
      reference_cellphone_phone_type_id: phoneList[1]?.type
        ?.reference_phone_type_id
        ? phoneList[1]?.type?.reference_phone_type_id
        : phoneTypeList[0]?.reference_phone_type_id,
      is_auto_notify: phoneList?.[0] ? (phoneList[0]?.auto_notify ? false : true) : true,
      is_cell_auto_notify: phoneList?.[1] ? (phoneList[1]?.auto_notify ? false : true) : true,
    };

    const apiParams = {
      dispatch,
      history,
      setSnackBarMessage: onApiResponse,
      setChangesSaveModal,
      payload,
      medical_alert_user_id: medicalUserDetail?.medical_alert_user_id,
      userType: userData?.isGuestUser ? "guestuser" : "accountowner",
      engagementType: "Update Subscriber Detail",
    };

    updateMedicalAddress(apiParams);
  }
};

/**
 * Handle phone list changes and validation
 */
export const handlePhoneListChange = (newPhoneList, params) => {
  const {
    setPhoneList,
    validationTriggered,
    errorState,
    setErrorState,
    snackbarState,
    onApiResponse,
    dismissSnackbar,
    VALIDATION_FIELDS
  } = params;

  setPhoneList(newPhoneList);

  if (validationTriggered) {
    const hasPhoneError = newPhoneList?.length === 0;

    if (hasPhoneError !== errorState.fieldErrors?.phoneList) {
      setErrorState(prev => ({
        ...prev,
        fieldErrors: { ...prev.fieldErrors, phoneList: hasPhoneError },
        fieldMessages: { ...prev.fieldMessages, phoneList: hasPhoneError ? FIELD_CONFIG.phoneList?.message || "At least one phone number is required" : "" }
      }));

      if (hasPhoneError && !snackbarState.show) {
        onApiResponse(alerts?.incorrect_or_missing_details, "error");
      }
    }
  }
};

